import { useRequest } from 'ahooks';
import { Skeleton } from 'antd';
import Ribbon from 'assets/ribbon.svg?react';
import PageTopBar from 'components/navigation/PageTopBar';
import PostsList from 'components/PostsList';
import { colors } from 'design';
import useScrollRestoration from 'hooks/useScrollRestoration';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiContentManager from 'services/api/ApiContentManager';
import { PostDTO } from 'services/api/models/PostDTO';
import { routes } from 'services/RouteService';

// The editorial selection, displayed section by section in this exact order.
const OCTOBRE_ROSE_SECTIONS: { titleKey: string; postIds: number[] }[] = [
  { titleKey: 'octobreRose.sections.understand', postIds: [2, 3] },
  { titleKey: 'octobreRose.sections.risk', postIds: [15, 110] },
  { titleKey: 'octobreRose.sections.recognise', postIds: [23, 4, 67] },
  { titleKey: 'octobreRose.sections.screening', postIds: [114, 115] },
  { titleKey: 'octobreRose.sections.diagnosis', postIds: [111] },
  { titleKey: 'octobreRose.sections.treatments', postIds: [112] },
  { titleKey: 'octobreRose.sections.afterCancer', postIds: [113] },
];
const OCTOBRE_ROSE_POST_IDS = OCTOBRE_ROSE_SECTIONS.flatMap(section => section.postIds);

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: colors.surface.page,
    alignItems: 'center',
  },
  container: {
    padding: '16px 8px 16px',
    height: '100%',
    overflowY: 'scroll',
    maxWidth: 600,
    width: '100%',
  },
  descriptionContainer: {
    marginTop: 48,
    marginBottom: 32,
  },
  description: {
    paddingLeft: 8,
    paddingRight: 8,
    color: colors.text.secondary,
  },
  sectionTitle: {
    margin: '24px 8px 8px',
    color: colors.text.secondary,
  },
  postsSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
});

export default function OctobreRosePage() {
  const { t } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const scrollableRef = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollableRef);

  const { data: posts, loading } = useRequest(() => ApiContentManager.getPostsByIds(OCTOBRE_ROSE_POST_IDS), {
    cacheKey: 'content.octobreRosePosts',
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });

  // Sections keep the editorial order; posts that don't exist or are drafts are
  // skipped, and so is a section left without any post.
  const sections = useMemo(() => {
    const postsById = new Map((posts ?? []).map(post => [post.id, post]));
    return OCTOBRE_ROSE_SECTIONS.map(section => ({
      titleKey: section.titleKey,
      posts: section.postIds.map(id => postsById.get(id)).filter((post): post is PostDTO => post !== undefined),
    })).filter(section => section.posts.length > 0);
  }, [posts]);

  const back = () => {
    // Opened directly (e.g. from a shared link), there is no in-app page to go back to.
    if (location.key === 'default') {
      navigate(routes.home.route);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <PageTopBar
        title={t('octobreRose.title')}
        backButton
        onBackButtonClicked={back}
        style={{ background: colors.gradient.highlight.value }}
      />
      <div className={styles.container} ref={scrollableRef}>
        <div className={styles.descriptionContainer}>
          <Ribbon aria-hidden="true" width={48} height={48} />
          <p className={styles.description}>{t('home.octobreRose.subtitle')}</p>
        </div>
        {loading && !posts ? (
          <div className={styles.postsSkeleton}>
            <Skeleton.Input block active style={{ height: 64 }} />
            <Skeleton.Input block active style={{ height: 64 }} />
            <Skeleton.Input block active style={{ height: 64 }} />
          </div>
        ) : sections.length === 0 ? (
          <PostsList posts={[]} />
        ) : (
          sections.map(section => (
            <section key={section.titleKey} data-testid="octobre-rose-section">
              <h3 className={styles.sectionTitle}>{t(section.titleKey)}</h3>
              <PostsList posts={section.posts} posthogOnArticleOpenExtras={{ opened_on_page: 'octobre-rose' }} />
            </section>
          ))
        )}
      </div>
    </div>
  );
}
